import {Order} from 'app/models/Order';
import {
  CreateTicketBody,
  FuroFormItem,
  FuroItemInput,
  FuroReturnItemInput,
  FuroReturnsResult,
  OpenFuroReturnsBody,
  Ticket,
  TicketCategory,
  TicketChannel,
  TicketNature,
  TicketSegment,
  TicketStatus,
} from './models/Ticket';
import {carriesFuroItems, FURO_TEXTS, FURO_TITLE_PREFIXES} from './ticketsMeta';

// Itens marcados do seletor de furo → furo_items do payload. Mantém só id
// numérico/nome/quantidade obrigatórios; código, marca e preço unitário só quando
// existem. O front NÃO envia return_protocol (o serviço de tickets resolve no autopart return).
export const furoItemsFromForm = (items: FuroFormItem[]): FuroItemInput[] =>
  items
    .filter((it) => it.selected && it.quantity > 0)
    .map((it) => {
      const fi: FuroItemInput = {
        order_item_id: it.order_item_id,
        nome_peca: it.nome_peca,
        quantity: it.quantity,
      };
      if (it.codigo_fabrica) fi.codigo_fabrica = it.codigo_fabrica;
      if (it.marca) fi.marca = it.marca;
      if (it.product_unit_price != null)
        fi.product_unit_price = it.product_unit_price;
      return fi;
    });

export interface NewTicketFormValues {
  title: string;
  description: string;
  channel: string;
  category: string;
  nature: string;
  segment: string;
  isB2C: boolean;
  contact_name: string;
  contact_phone: string;
  account_uid: string;
  account_name: string;
  order_uid: string;
  order_reference_id: string;
  order_value: string;
  assignee_uid: string;
  assignee_name: string;
  tagsText: string;
  furoItems: FuroFormItem[];
  // Furo diferido (opt-in): cria o furo SEM abrir returns na hora. Os campos de
  // contexto abaixo então não são enviados (o backend os ignora nesse modo).
  deferReturns: boolean;
  // Contexto do furo, espelhado do pedido (consumidor final ≠ oficina do ticket).
  order_created_at: string;
  customer_name: string;
  client_account_uid: string;
  // Método de pagamento do pedido (1 por request) — vem do detalhe via FuroEstoqueItems.
  order_payment_method: string;
  // Desconto do pedido (base, 1 por request) — espelhado do detalhe. `''` = ainda
  // não carregado; número (inclusive 0) = valor do pedido.
  discount_percentage: number | '';
}

export const buildBody = (v: NewTicketFormValues): CreateTicketBody => {
  const tags = v.tagsText
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
  const body: CreateTicketBody = {
    title: v.title.trim(),
    channel: v.channel as TicketChannel,
    category: v.category as TicketCategory,
    nature: v.nature as TicketNature,
    segment: v.segment as TicketSegment,
  };
  if (v.description.trim()) body.description = v.description.trim();
  if (v.isB2C) {
    if (v.contact_name.trim()) body.contact_name = v.contact_name.trim();
    if (v.contact_phone.trim()) body.contact_phone = v.contact_phone.trim();
  } else if (v.account_uid) {
    body.account_uid = v.account_uid;
    if (v.account_name) body.account_name = v.account_name;
  }
  if (v.order_uid) {
    body.order_uid = v.order_uid;
    if (v.order_reference_id) body.order_reference_id = v.order_reference_id;
    if (v.order_value) body.order_value = Number(v.order_value);
  }
  if (v.assignee_uid) {
    body.assignee_uid = v.assignee_uid;
    if (v.assignee_name) body.assignee_name = v.assignee_name;
  }
  if (tags.length) body.tags = tags;
  // Categorias que carregam itens: só os itens marcados, com id numérico do order item.
  // Sem return_protocol — o serviço de tickets resolve no autopart return.
  // Junto vai o contexto que o /returns exige (data do pedido + consumidor final).
  if (carriesFuroItems(v.category)) {
    const furo = furoItemsFromForm(v.furoItems);
    if (furo.length) body.furo_items = furo;
    if (v.deferReturns) {
      // Diferido: cria sem abrir returns. O contexto do consumidor é ignorado
      // pelo backend agora (só exigido quando os returns forem abertos depois).
      body.defer_returns = true;
    } else {
      if (v.order_created_at) body.order_created_at = v.order_created_at;
      if (v.customer_name) body.customer_name = v.customer_name;
      if (v.client_account_uid) body.client_account_uid = v.client_account_uid;
      if (v.order_payment_method)
        body.order_payment_method = v.order_payment_method;
      // `!== ''` preserva o 0 (pedido sem desconto); enviado cru, como vem do backend.
      if (v.discount_percentage !== '')
        body.discount_percentage = Number(v.discount_percentage);
    }
  }
  return body;
};

// Decide o próximo título do form de novo ticket quando categoria/pedido mudam
// (usado pelo FuroAutofill do NewTicketForm). Só categorias com fluxo de itens
// (furo de estoque, cancelamento) ganham título automático, e só quando há
// referência do pedido. Sobrescreve título vazio OU gerado por QUALQUER categoria
// do fluxo — reconhecido pelo prefixo em FURO_TITLE_PREFIXES, não só o da categoria
// atual — senão trocar de furo para cancelamento deixaria o título antigo congelado
// por parecer escrito à mão. Nunca sobrescreve um título que o usuário digitou.
// Devolve `null` quando o título deve ficar como está.
export const nextAutoTitle = (
  category: string,
  orderReferenceId: string,
  title: string
): string | null => {
  if (!carriesFuroItems(category) || !orderReferenceId) return null;
  const auto = `${FURO_TEXTS[category].titlePrefix}${orderReferenceId}`;
  const isAuto = FURO_TITLE_PREFIXES.some((p) => title.startsWith(p));
  return (!title || isAuto) && title !== auto ? auto : null;
};

// Método de pagamento do pedido para o furo (pix/credit_card/bankslip). No response
// real `meta.data.payment_data` é um ARRAY (o model declara objeto), daí o cast —
// mesmo tratamento da visão unificada (DetailDrawer). `payment_method` é o campo com
// pix/credit_card/bankslip; `payment_model` é o tipo de cobrança (pay_now etc.).
export const orderPaymentMethod = (order?: Order): string | undefined => {
  const raw = order?.meta?.data?.payment_data as unknown as
    | {payment_method?: string}
    | {payment_method?: string}[]
    | undefined;
  const pd = Array.isArray(raw) ? raw[0] : raw;
  return pd?.payment_method || undefined;
};

// Desconto do pedido para o furo — mesmo objeto/cast do orderPaymentMethod
// (payment_data é ARRAY no response real). Enviado CRU, como vem do backend (ex.:
// 0.05), sem transformar em percentual inteiro. `0` (sem desconto) é valor válido,
// então NÃO se usa `valor || undefined` (que o engoliria) — só ausência vira undefined.
export const orderDiscountPercentage = (order?: Order): number | undefined => {
  const raw = order?.meta?.data?.payment_data as unknown as
    | {discount_percentage?: number}
    | {discount_percentage?: number}[]
    | undefined;
  const pd = Array.isArray(raw) ? raw[0] : raw;
  return typeof pd?.discount_percentage === 'number'
    ? pd.discount_percentage
    : undefined;
};

// Contexto do consumidor final exigido pela rota furo-returns, lido da LISTA de
// pedidos (getOrders por referenceId) — mesma fonte confiável do OrderField, pois o
// detalhe (showOrder) nem sempre traz o client_account aninhado. Acha o pedido por
// uid; devolve strings vazias quando não encontra (o chamador bloqueia a ação).
export interface FuroReturnContext {
  customer_name: string;
  client_account_uid: string;
  order_created_at: string;
}

export const furoReturnContextFromOrders = (
  orders: Order[],
  orderUid: string
): FuroReturnContext => {
  const order = orders.find((o) => o.uid === orderUid);
  if (!order)
    return {customer_name: '', client_account_uid: '', order_created_at: ''};
  return {
    customer_name: order.client_account?.name ?? '',
    client_account_uid:
      order.client_account_uid ?? order.client_account?.uid ?? '',
    order_created_at: order.created_at ? String(order.created_at) : '',
  };
};

// Monta o corpo de POST /tickets/:uid/furo-returns para UM item (um botão por item).
// product_unit_price entra quando presente (inclusive 0); order_payment_method só
// quando não vazio; discount_percentage é base (1 por request, vale p/ todos os itens)
// e entra quando presente (inclusive 0). O consumidor final vem do contexto
// auto-derivado + confirmado.
export const openFuroReturnsBody = (
  item: FuroReturnItemInput,
  ctx: FuroReturnContext & {
    order_payment_method?: string;
    discount_percentage?: number;
  }
): OpenFuroReturnsBody => {
  const it: FuroReturnItemInput = {order_item_id: item.order_item_id};
  if (item.product_unit_price != null)
    it.product_unit_price = item.product_unit_price;
  const body: OpenFuroReturnsBody = {
    items: [it],
    customer_name: ctx.customer_name,
    client_account_uid: ctx.client_account_uid,
    order_created_at: ctx.order_created_at,
  };
  if (ctx.order_payment_method)
    body.order_payment_method = ctx.order_payment_method;
  if (ctx.discount_percentage != null)
    body.discount_percentage = ctx.discount_percentage;
  return body;
};

// A forma exata da resposta 200 de furo-returns não foi fixada no contrato: aceita
// tanto o ticket "achatado" ({...ticket, furo_failures}) quanto o wrapper
// ({ticket, furo_failures}). Sempre devolve {ticket, furo_failures} (failures → []).
export const normalizeFuroReturnsResponse = (data: any): FuroReturnsResult => {
  const ticket: Ticket = data?.ticket ?? data;
  const furo_failures = data?.furo_failures ?? ticket?.furo_failures ?? [];
  return {ticket, furo_failures};
};

export interface SlaChip {
  state: 'ok' | 'warn' | 'late';
  hours: number; // horas absolutas (restantes se não atrasado; vencidas se atrasado)
  late: boolean;
}

export const slaChip = (
  dueAt: string | null,
  now: Date = new Date()
): SlaChip | null => {
  if (!dueAt) return null;
  const diffH = (new Date(dueAt).getTime() - now.getTime()) / 3600_000;
  if (diffH < 0)
    return {state: 'late', hours: Math.round(Math.abs(diffH)), late: true};
  if (diffH <= 4) return {state: 'warn', hours: Math.round(diffH), late: false};
  return {state: 'ok', hours: Math.round(diffH), late: false};
};

// Espelho da máquina de status do servidor (spec §3) para a UI só oferecer movimentos válidos.
const TRANSITIONS: Record<TicketStatus, TicketStatus[]> = {
  aberto: ['em_andamento', 'aguardando_cliente', 'resolvido'],
  em_andamento: ['aguardando_cliente', 'resolvido', 'fechado'],
  aguardando_cliente: ['em_andamento', 'resolvido', 'fechado'],
  resolvido: ['fechado', 'em_andamento'],
  fechado: [],
};

export const validTransitions = (status: TicketStatus): TicketStatus[] =>
  TRANSITIONS[status];

// Default best-effort; platinum/basic + select/seguradora/marketplace são política de CX (spec §13).
const LEVEL_TO_SEGMENT: Record<string, TicketSegment> = {
  bronze: 'bronze',
  silver: 'prata',
  gold: 'ouro',
  diamond: 'diamante',
};

export const segmentFromLevel = (level?: string): TicketSegment | undefined =>
  level ? LEVEL_TO_SEGMENT[level] : undefined;

// Rótulo do nível real da oficina (customer_level) — mais amplo que o segmento
// do ticket: cobre platinum/basic, que não viram segmento.
const LEVEL_LABEL: Record<string, string> = {
  bronze: 'Bronze',
  silver: 'Prata',
  gold: 'Ouro',
  platinum: 'Platina',
  diamond: 'Diamante',
  basic: 'Básico',
};

export const levelLabel = (level?: string): string | undefined =>
  level ? LEVEL_LABEL[level] ?? level : undefined;

export interface TicketFilterInput {
  status?: string[];
  priority?: string[];
  channel?: string[];
  category?: string[];
  nature?: string[];
  segment?: string[];
  assignee_uid?: string[];
  tag?: string[];
  account_uid?: string;
  order_uid?: string;
  // Instantes ISO; limite superior EXCLUSIVO (ver ticketsPeriod.ts).
  created_from?: string;
  created_to?: string;
  q?: string;
  is_open?: boolean;
  needs_action?: boolean;
  response_overdue?: boolean;
  order?: string;
  limit?: number;
  offset?: number;
}

// Facetas vindas do popover de filtros (subconjunto multi-seleção do filtro).
type TicketFacetsInput = Pick<
  TicketFilterInput,
  'status' | 'priority' | 'channel' | 'category' | 'nature' | 'segment' | 'tag'
>;

/**
 * Monta o filtro da API a partir do estado da UI (facetas + responsável + busca).
 *
 * Regra do `is_open`: o board mostra só abertos *por padrão*, mas a **busca textual**
 * (oficina, pedido, tag) precisa varrer **todos os status** — inclusive `fechado` — para
 * não voltar vazia. Por isso o default `is_open=true` só vale quando NÃO há busca nem
 * status explícito. Assim, buscar por nome da oficina / id do pedido não exige escolher
 * status/prioridade (lista e quadro).
 */
export const buildTicketFilters = ({
  facets,
  assigneeUid,
  myUid,
  q,
  needsActionOnly = false,
  responseOverdueOnly = false,
  period,
}: {
  facets: TicketFacetsInput;
  assigneeUid?: string;
  myUid?: string;
  q?: string;
  needsActionOnly?: boolean;
  responseOverdueOnly?: boolean;
  period?: {created_from?: string; created_to?: string};
}): TicketFilterInput => {
  const search = q?.trim();
  const hasStatus = !!facets.status?.length;
  const constrainToOpen = !hasStatus && !search;
  // "Precisa de ação" implica minha ação; senão usa a seleção do dropdown de responsável.
  const scopedAssignee = needsActionOnly ? myUid : assigneeUid;
  return {
    ...(constrainToOpen ? {is_open: true} : {}),
    // Na fila de sem-resposta a ordem útil é o mais abandonado primeiro, não o mais
    // recente: quem está há mais tempo no silêncio é quem precisa aparecer no topo.
    order: responseOverdueOnly ? 'response_due_at.asc' : 'updated_at.desc',
    ...(scopedAssignee ? {assignee_uid: [scopedAssignee]} : {}),
    ...(needsActionOnly ? {needs_action: true} : {}),
    ...(responseOverdueOnly ? {response_overdue: true} : {}),
    ...(period ?? {}),
    ...facets,
    ...(search ? {q: search} : {}),
  };
};

/**
 * Tickets abertos do MESMO pedido — a lista do aviso de duplicidade (item 4).
 *
 * O filtro por `order_uid` é defesa, não redundância: se a API ignorar o query param,
 * a resposta vem com a fila inteira e o aviso acusaria duplicata em TODO ticket. O time
 * aprenderia a ignorá-lo em uma semana. Com o filtro, um param ignorado vira no-op.
 */
export const openTicketsForOrder = (
  items: Ticket[],
  orderUid: string,
  max = 5
): Ticket[] =>
  items
    .filter((t) => t.order_uid === orderUid && t.status !== 'fechado')
    .slice(0, max);

// Selo "precisa da MINHA ação": campo do backend + atribuído a mim.
export const needsMyAction = (ticket: Ticket, myUid?: string): boolean =>
  !!ticket.needs_action && !!myUid && ticket.assignee_uid === myUid;

// Normaliza um telefone para o formato que o wa.me espera (só dígitos, com DDI
// Brasil). Retorna null quando curto demais para virar link (cai pra só-texto).
export const toWhatsAppNumber = (raw?: string | null): string | null => {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 10) return null;
  if (digits.startsWith('55')) return digits;
  if (digits.length === 10 || digits.length === 11) return `55${digits}`;
  return digits;
};

export const serializeTicketFilters = (f: TicketFilterInput): string => {
  const p = new URLSearchParams();
  const multi: (keyof TicketFilterInput)[] = [
    'status',
    'priority',
    'channel',
    'category',
    'nature',
    'segment',
    'assignee_uid',
    'tag',
  ];
  multi.forEach((k) => {
    const v = f[k] as string[] | undefined;
    if (v && v.length) p.set(k, v.join(','));
  });
  if (f.account_uid) p.set('account_uid', f.account_uid);
  if (f.order_uid) p.set('order_uid', f.order_uid);
  if (f.created_from) p.set('created_from', f.created_from);
  if (f.created_to) p.set('created_to', f.created_to);
  if (f.q) p.set('q', f.q);
  if (f.is_open) p.set('is_open', 'true'); // is_open=false não existe (spec §11)
  if (f.needs_action) p.set('needs_action', 'true');
  // Sem `false`, mesma regra do is_open (spec §7.1).
  if (f.response_overdue) p.set('response_overdue', 'true');
  if (f.order) p.set('order', f.order);
  if (f.limit != null) p.set('limit', String(f.limit));
  if (f.offset != null) p.set('offset', String(f.offset));
  return p.toString();
};

/**
 * Oficina e pedido FIXOS do formulário de novo ticket. Quem abre um ticket já
 * dentro de um pedido (Visão operacional) não procura a oficina nem o pedido: os
 * dois vêm prontos e não são editáveis. Isso não é só conforto — é o que evita
 * montar o picker de pedido, que custa uma consulta nova no banco do core
 * (spec 2026-09-19, D2).
 */
export interface LockedOrder {
  accountUid: string;
  accountName: string;
  orderUid: string;
  orderReferenceId: string;
  /**
   * REAIS com duas casas ("1234.50") — o `buildBody` faz `Number()` nisso. A
   * UNIDADE é a mesma que o picker de pedido escreve no campo a partir de
   * `total_client_price`, mas a DEFINIÇÃO nem sempre é: quando `orderValue`
   * vem da Visão operacional (`operationalOverview/domain/newTicketSeed.ts`),
   * é a soma de `client_unit_price × total_quantity` só dos itens que a fonte
   * daquela passada carregou — um subconjunto do pedido nas ramificações de
   * solicitação de compra e entrega, sem frete nem desconto do pedido em
   * nenhuma delas. Fica perto de `total_client_price`, não sempre igual a
   * ele — risco aceito e documentado (spec 2026-09-19, D15).
   */
  orderValue: string;
  /** Contexto que o `/returns` exige nas categorias com itens. */
  orderCreatedAt: string;
  customerName: string;
  clientAccountUid: string;
  /** Derivado do nível da oficina; ausente = o `SegmentAutofill` resolve. */
  segment?: TicketSegment;
}
